/**
 * Reusable "Which key do I use?" callout — the single most important piece of
 * cross-plane wayfinding in the docs. PokerWorks has three credential prefixes
 * across two planes, and confusing them is the most common first-integration
 * mistake. Drop <WhichKey /> on Get started, Authentication, and Service
 * accounts; pass `highlight` to emphasize the key that page is about.
 */
type KeyPrefix = 'pw_test_' | 'pw_live_' | 'pw_mgmt_';

const ROWS: ReadonlyArray<{ plane: string; prefix: KeyPrefix; use: string }> = [
  { prefix: 'pw_test_', plane: 'Calculation API', use: 'Server-side test calculation calls.' },
  { prefix: 'pw_live_', plane: 'Calculation API', use: 'Server-side live calculation calls.' },
  {
    prefix: 'pw_mgmt_',
    plane: 'Automation & management',
    use: 'Server-side service-account automation. Never calculation calls or browser code.',
  },
];

export function WhichKey({ highlight }: { highlight?: KeyPrefix }) {
  return (
    <div
      className="pw-which-key"
      role="note"
      aria-label="Which key do I use?"
      style={{
        border: '1px solid var(--pw-docs-line, #e8e2d7)',
        borderRadius: 10,
        margin: '1.25rem 0',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: 'var(--pw-docs-paper-soft, #fbfaf7)',
          borderBottom: '1px solid var(--pw-docs-line, #e8e2d7)',
          fontSize: '0.8rem',
          fontWeight: 700,
          letterSpacing: '0.01em',
          padding: '0.55rem 0.85rem',
        }}
      >
        Which key do I use?
      </div>
      <div>
        {ROWS.map((row) => {
          const on = highlight === row.prefix;
          return (
            <div
              key={row.prefix}
              style={{
                alignItems: 'baseline',
                borderTop: '1px solid var(--pw-docs-line, #e8e2d7)',
                display: 'grid',
                gap: '0.25rem 1rem',
                gridTemplateColumns: 'minmax(7.5rem, auto) 1fr',
                padding: '0.6rem 0.85rem',
                background: on ? 'rgba(183, 129, 47, 0.08)' : 'transparent',
                boxShadow: on ? 'inset 3px 0 0 var(--pw-docs-gold, #b7812f)' : 'none',
              }}
            >
              <code
                style={{
                  color: 'var(--pw-docs-ink, #241b14)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {row.prefix}…
              </code>
              <span style={{ fontSize: '0.85rem' }}>
                <strong style={{ color: 'var(--pw-docs-green, #315f48)' }}>{row.plane}</strong>
                {' — '}
                {row.use}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
